// ============================================
// @gs/ui — KpiCard
// Design : bg-white, icône dans cercle coloré
// Source de vérité : docs/DESIGN_SYSTEM_GS.md
// ============================================

import React from 'react';
import type { LucideIcon } from 'lucide-react';

export interface KpiCardProps {
  label: string;
  value: string | number;
  icon: LucideIcon;
  /** @deprecated Utiliser `brand` — alias conservé 1 release pour compatibilité appelants. */
  variant?: 'blue' | 'green' | 'brand' | 'orange' | 'red';
}

const brandIconStyle = {
  iconBg: 'bg-brand-50 dark:bg-brand-950/40',
  iconColor: 'text-brand-600 dark:text-brand-400',
};

const iconStyles: Record<string, { iconBg: string; iconColor: string }> = {
  blue:   { iconBg: 'bg-blue-50 dark:bg-blue-950/40',     iconColor: 'text-blue-600 dark:text-blue-400' },
  green:  { iconBg: 'bg-green-50 dark:bg-green-950/40',   iconColor: 'text-green-600 dark:text-green-400' },
  brand:  brandIconStyle,
  orange: brandIconStyle,
  red:    { iconBg: 'bg-red-50 dark:bg-red-950/40',       iconColor: 'text-red-600 dark:text-red-400' },
};

/**
 * Card KPI — toujours fond blanc, icône dans pastille colorée.
 * ⚠️  Ne jamais mettre le fond coloré sur toute la carte.
 */
export function KpiCard({ label, value, icon: Icon, variant = 'blue' }: KpiCardProps) {
  const { iconBg, iconColor } = iconStyles[variant];
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm p-4 flex items-center gap-4 kpi-hover">
      <div className={`p-3 rounded-xl shrink-0 ${iconBg}`}>
        <Icon size={22} className={iconColor} />
      </div>
      <div>
        <p className="text-meta font-medium text-slate-500 dark:text-slate-400">{label}</p>
        <p className="text-kpi font-bold text-slate-800 dark:text-slate-100 leading-none mt-0.5 tabular-nums">{value}</p>
      </div>
    </div>
  );
}
